function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseInline(text) {
  let parsed = escapeHtml(text);

  parsed = parsed.replace(
    /`([^`]+)`/g,
    '<code class="md-inline-code">$1</code>'
  );
  parsed = parsed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
    const safeUrl = escapeHtml(url);
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  parsed = parsed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  parsed = parsed.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  return parsed;
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/<[^>]+>/g, '')
    .replace(/[^\w\s-가-힣]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function createHeadingIdFactory() {
  const counts = {};

  return text => {
    const base = slugify(text) || 'section';
    counts[base] = (counts[base] || 0) + 1;
    return counts[base] === 1 ? base : `${base}-${counts[base]}`;
  };
}

function isTableDivider(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function isTableRow(line) {
  return line.includes('|') && !isTableDivider(line);
}

function parseTableRow(line) {
  let row = line.trim();

  if (row.startsWith('|')) {
    row = row.slice(1);
  }

  if (row.endsWith('|')) {
    row = row.slice(0, -1);
  }

  return row.split('|').map(cell => cell.trim());
}

function parseTableCell(cell) {
  const attributes = {
    colspan: 1,
    rowspan: 1,
    align: '',
    valign: '',
    skip: cell.trim() === '[[skip]]'
  };
  const allowedAlign = ['left', 'center', 'right'];
  const allowedValign = ['top', 'middle', 'bottom'];
  const cleaned = cell.replace(/\[\[([^\]]+)\]\]/g, (_, rawAttributes) => {
    rawAttributes.split(',').forEach(attribute => {
      const [name, value] = attribute.split('=').map(part => part.trim());
      const normalizedName = name.toLowerCase();
      const normalizedValue = String(value || '').toLowerCase();
      const numberValue = Number(value);

      if (
        ['colspan', 'rowspan'].includes(normalizedName) &&
        Number.isInteger(numberValue) &&
        numberValue > 1
      ) {
        attributes[normalizedName] = numberValue;
      }

      if (normalizedName === 'align' && allowedAlign.includes(normalizedValue)) {
        attributes.align = normalizedValue;
      }

      if (
        normalizedName === 'valign' &&
        allowedValign.includes(normalizedValue)
      ) {
        attributes.valign = normalizedValue;
      }
    });

    return '';
  });

  return {
    text: cleaned.trim(),
    attributes
  };
}

function renderTableCell(tagName, cell) {
  const attrs = [];
  const classes = [];

  if (cell.attributes.colspan > 1) {
    attrs.push(`colspan="${cell.attributes.colspan}"`);
  }

  if (cell.attributes.rowspan > 1) {
    attrs.push(`rowspan="${cell.attributes.rowspan}"`);
  }

  if (cell.attributes.align) {
    classes.push(`md-table-align-${cell.attributes.align}`);
  }

  if (cell.attributes.valign) {
    classes.push(`md-table-valign-${cell.attributes.valign}`);
  }

  if (classes.length) {
    attrs.push(`class="${classes.join(' ')}"`);
  }

  return `<${tagName}${attrs.length ? ` ${attrs.join(' ')}` : ''}>${parseInline(
    cell.text
  )}</${tagName}>`;
}

function renderTableRows(rows, tagName) {
  const activeRowspans = [];

  return rows
    .map(row => {
      let columnIndex = 0;
      const newlyOccupiedColumns = new Set();
      const cells = row.map(rawCell => {
        const skippedColumns = columnIndex;

        while (activeRowspans[columnIndex] > 0) {
          columnIndex += 1;
        }

        const skippedByRowspan = columnIndex > skippedColumns;
        if (skippedByRowspan && rawCell.trim() === '') {
          return '';
        }

        const cell = parseTableCell(rawCell);

        if (cell.attributes.skip) {
          columnIndex += 1;
          return '';
        }

        if (cell.attributes.rowspan > 1) {
          for (let index = 0; index < cell.attributes.colspan; index += 1) {
            const occupiedColumn = columnIndex + index;
            activeRowspans[occupiedColumn] = cell.attributes.rowspan - 1;
            newlyOccupiedColumns.add(occupiedColumn);
          }
        }

        columnIndex += cell.attributes.colspan;
        return renderTableCell(tagName, cell);
      });

      activeRowspans.forEach((rowsLeft, index) => {
        if (rowsLeft > 0 && !newlyOccupiedColumns.has(index)) {
          activeRowspans[index] = rowsLeft - 1;
        }
      });

      return `<tr>${cells.join('')}</tr>`;
    })
    .join('');
}

function renderTable(tableLines) {
  const headerRows = [parseTableRow(tableLines[0])];
  const bodyRows = tableLines.slice(2).map(parseTableRow);

  return [
    '<div class="md-table-wrap"><table class="md-table">',
    `<thead>${renderTableRows(headerRows, 'th')}</thead>`,
    `<tbody>${renderTableRows(bodyRows, 'td')}</tbody>`,
    '</table></div>'
  ].join('');
}

function getYoutubeVideoId(value) {
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.replace(/^www\./, '');

    if (hostname === 'youtu.be') {
      return url.pathname.split('/').filter(Boolean)[0] || '';
    }

    if (!['youtube.com', 'm.youtube.com'].includes(hostname)) {
      return '';
    }

    if (url.pathname === '/watch') {
      return url.searchParams.get('v') || '';
    }

    const [, type, id] = url.pathname.split('/');

    if (['embed', 'shorts'].includes(type)) {
      return id || '';
    }
  } catch (error) {
    return '';
  }

  return '';
}

function renderYoutubeEmbed(line) {
  const videoId = getYoutubeVideoId(line);

  if (!videoId || !/^[\w-]{6,}$/.test(videoId)) {
    return '';
  }

  return `<div class="md-youtube-embed"><iframe src="https://www.youtube.com/embed/${escapeHtml(
    videoId
  )}" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
}

export function extractMarkdownHeadings(source) {
  if (!source) return [];

  const createId = createHeadingIdFactory();

  return source
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(line => line.match(/^(#{1,3})\s+(.*)$/))
    .filter(Boolean)
    .map(match => ({
      level: match[1].length,
      text: match[2].trim(),
      id: createId(match[2].trim())
    }));
}

export function renderMarkdown(source) {
  if (!source) return '';

  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const html = [];
  let paragraph = [];
  let listItems = [];
  let tableLines = [];
  let skipNextTableDivider = false;
  let inCodeBlock = false;
  let codeBuffer = [];
  const createId = createHeadingIdFactory();

  const flushParagraph = () => {
    if (!paragraph.length) return;
    html.push(`<p>${parseInline(paragraph.join(' '))}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    html.push(
      `<ul>${listItems
        .map(item => `<li>${parseInline(item)}</li>`)
        .join('')}</ul>`
    );
    listItems = [];
  };

  const flushTable = () => {
    if (!tableLines.length) return;

    html.push(renderTable(tableLines));
    tableLines = [];
  };

  const flushCodeBlock = () => {
    if (!codeBuffer.length) return;
    html.push(
      `<pre class="md-code-block"><code>${escapeHtml(
        codeBuffer.join('\n')
      )}</code></pre>`
    );
    codeBuffer = [];
  };

  lines.forEach((line, index) => {
    if (skipNextTableDivider && isTableDivider(line)) {
      skipNextTableDivider = false;
      return;
    }

    if (line.trim().startsWith('```')) {
      flushParagraph();
      flushList();
      flushTable();

      if (inCodeBlock) {
        flushCodeBlock();
      }

      inCodeBlock = !inCodeBlock;
      return;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      flushTable();
      return;
    }

    const youtubeEmbed = renderYoutubeEmbed(line.trim());
    if (youtubeEmbed) {
      flushParagraph();
      flushList();
      flushTable();
      html.push(youtubeEmbed);
      return;
    }

    const nextLine = lines[index + 1] || '';
    if (!tableLines.length && isTableRow(line) && isTableDivider(nextLine)) {
      flushParagraph();
      flushList();
      tableLines.push(line, nextLine);
      skipNextTableDivider = true;
      return;
    }

    if (tableLines.length) {
      if (isTableRow(line)) {
        tableLines.push(line);
        return;
      }

      flushTable();
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      flushTable();
      const level = headingMatch[1].length;
      const headingText = headingMatch[2].trim();
      const headingId = createId(headingText);
      html.push(
        `<h${level} id="${headingId}">${parseInline(headingText)}</h${level}>`
      );
      return;
    }

    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();
      flushTable();
      html.push(`<blockquote>${parseInline(quoteMatch[1])}</blockquote>`);
      return;
    }

    const orderedListMatch = line.match(/^\d+\.\s+(.*)$/);
    if (orderedListMatch) {
      listItems.push(orderedListMatch[1]);
      return;
    }

    const unorderedListMatch = line.match(/^[-*]\s+(.*)$/);
    if (unorderedListMatch) {
      listItems.push(unorderedListMatch[1]);
      return;
    }

    flushList();
    paragraph.push(line.trim());
  });

  flushParagraph();
  flushList();
  flushTable();

  if (inCodeBlock) {
    flushCodeBlock();
  }

  return html.join('');
}
